import { useEffect, useMemo, useState } from "react";
import { Loader2, Star, Truck, Users, X } from "lucide-react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TourneeSelect } from "@/components/map/ZoneDialog";
import { textOn } from "@/lib/mapStyle";

/** Saisie guidée du téléphone : chiffres uniquement, points ajoutés tout seuls (##.##.##.##.##). */
export function formatTelInput(value) {
  const digits = String(value || "").replace(/\D/g, "").slice(0, 10);
  return digits.replace(/(\d{2})(?=\d)/g, "$1.");
}

const EMPTY = { role: "", nom: "", prenom: "", telephone: "", sst: "", tournees: [], tournee_defaut: null, notes: "" };
const NONE = "__none__";

export function LivreurForm({ open, livreur, tournees, ssts, onClose, onSave }) {
  const isNew = !livreur?.id;
  const [f, setF] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");
  const [pick, setPick] = useState(null);

  useEffect(() => {
    if (!open) return;
    setF(livreur ? { ...EMPTY, ...livreur, telephone: formatTelInput(livreur.telephone) } : EMPTY);
    setError("");
    setPick(null);
    setSaving(false);
  }, [open, livreur]);

  const tByCode = useMemo(() => Object.fromEntries(tournees.map((t) => [t.code, t])), [tournees]);
  const disponibles = tournees.filter((t) => !f.tournees.includes(t.code));
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  const addTournee = (code) => {
    if (!code || f.tournees.includes(code)) return;
    setF((x) => {
      const list = [...x.tournees, code];
      // Une seule tournée : c'est la tournée principale. Plusieurs : on garde
      // le choix déjà fait, sinon la première ajoutée.
      return { ...x, tournees: list, tournee_defaut: x.tournee_defaut && list.includes(x.tournee_defaut) ? x.tournee_defaut : list[0],
               sst: x.sst || tByCode[code]?.sst || "" };
    });
    setPick(null);
  };
  const removeTournee = (code) => setF((x) => {
    const list = x.tournees.filter((c) => c !== code);
    return { ...x, tournees: list, tournee_defaut: list.includes(x.tournee_defaut) ? x.tournee_defaut : list[0] || null };
  });

  const telDigits = f.telephone.replace(/\D/g, "");
  const valid = f.role && f.nom.trim() && f.prenom.trim() && (telDigits.length === 0 || telDigits.length === 10);

  const submit = async (e) => {
    e.preventDefault();
    if (!valid) return;
    setSaving(true);
    setError("");
    try {
      await onSave({ ...f, telephone: telDigits });
    } catch (err) {
      setError(err.message || String(err));
    } finally {
      setSaving(false);
    }
  };

  const RoleBtn = ({ value, icon: Icon, label, hint }) => (
    <button
      type="button"
      onClick={() => set("role", value)}
      className={`flex-1 text-left rounded-lg border p-3 transition-colors ${f.role === value ? "border-primary bg-primary/10" : "border-border hover:bg-accent"}`}
      data-testid={`role-${value}`}
    >
      <span className={`flex items-center gap-2 font-semibold text-sm ${f.role === value ? "text-primary" : ""}`}><Icon size={15} /> {label}</span>
      <span className="block text-[11px] text-muted-foreground mt-0.5">{hint}</span>
    </button>
  );

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="sm:max-w-lg max-h-[92dvh] overflow-y-auto" data-testid="livreur-form">
        <form onSubmit={submit} className="space-y-4">
          <DialogHeader>
            <DialogTitle>{isNew ? "Créer une fiche livreur" : `Modifier — ${livreur.prenom} ${livreur.nom}`}</DialogTitle>
            <DialogDescription>Les champs marqués * sont obligatoires.</DialogDescription>
          </DialogHeader>

          <div className="space-y-1.5">
            <Label>Type de chauffeur *</Label>
            <div className="flex gap-2">
              <RoleBtn value="principal" icon={Truck} label="Chauffeur principal" hint="Titulaire de la tournée" />
              <RoleBtn value="secondaire" icon={Users} label="Chauffeur secondaire" hint="Remplaçant / renfort" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="liv-nom">Nom *</Label>
              <Input id="liv-nom" value={f.nom} onChange={(e) => set("nom", e.target.value)} autoComplete="off" data-testid="liv-nom" />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="liv-prenom">Prénom *</Label>
              <Input id="liv-prenom" value={f.prenom} onChange={(e) => set("prenom", e.target.value)} autoComplete="off" data-testid="liv-prenom" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1.5">
              <Label htmlFor="liv-tel">Téléphone</Label>
              <Input
                id="liv-tel"
                inputMode="tel"
                placeholder="06.12.34.56.78"
                value={f.telephone}
                onChange={(e) => set("telephone", formatTelInput(e.target.value))}
                className="font-mono"
                data-testid="liv-tel"
              />
              {telDigits.length > 0 && telDigits.length < 10 && <p className="text-[11px] text-amber-500">{10 - telDigits.length} chiffre(s) manquant(s)</p>}
            </div>
            <div className="space-y-1.5">
              <Label>SST</Label>
              <Select value={f.sst || NONE} onValueChange={(v) => set("sst", v === NONE ? "" : v)}>
                <SelectTrigger data-testid="liv-sst"><SelectValue placeholder="Choisir la SST" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value={NONE}>— Aucune —</SelectItem>
                  {ssts.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="space-y-1.5">
            <Label>Tournées affectées</Label>
            <TourneeSelect
              tournees={disponibles}
              value={pick}
              onChange={addTournee}
              allowNone
              noneLabel="+ Ajouter une tournée…"
              testId="liv-add-tournee"
            />
            {f.tournees.length > 0 && (
              <div className="rounded-lg border border-border divide-y divide-border mt-2" data-testid="liv-tournees">
                {f.tournees.map((code) => {
                  const t = tByCode[code];
                  const isDef = f.tournee_defaut === code;
                  return (
                    <div key={code} className="flex items-center gap-2 px-2.5 py-2">
                      <span className="font-mono text-xs font-semibold px-1.5 py-0.5 rounded" style={{ background: t?.couleur || "#888", color: textOn(t?.couleur || "#888") }}>{code}</span>
                      <span className="text-xs text-muted-foreground truncate flex-1">{t?.libelle}</span>
                      {f.tournees.length > 1 ? (
                        <button
                          type="button"
                          onClick={() => set("tournee_defaut", code)}
                          className={`text-[11px] inline-flex items-center gap-1 px-2 py-1 rounded-md border ${isDef ? "border-primary text-primary bg-primary/10 font-semibold" : "border-border text-muted-foreground hover:bg-accent"}`}
                          data-testid={`liv-defaut-${code}`}
                        >
                          <Star size={11} fill={isDef ? "currentColor" : "none"} /> {isDef ? "Par défaut" : "Mettre par défaut"}
                        </button>
                      ) : (
                        <span className="text-[11px] inline-flex items-center gap-1 text-primary font-semibold"><Star size={11} fill="currentColor" /> Tournée principale</span>
                      )}
                      <button type="button" onClick={() => removeTournee(code)} className="p-1 rounded hover:bg-destructive/15 text-muted-foreground hover:text-destructive" aria-label={`Retirer ${code}`}>
                        <X size={14} />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="space-y-1.5">
            <Label htmlFor="liv-notes">Notes <span className="font-normal text-muted-foreground">(facultatif)</span></Label>
            <Textarea id="liv-notes" rows={2} value={f.notes} onChange={(e) => set("notes", e.target.value)} placeholder="Véhicule, horaires, consignes…" />
          </div>

          {error && <p className="text-sm text-destructive">{error}</p>}

          <DialogFooter>
            <Button type="button" variant="ghost" onClick={onClose}>Annuler</Button>
            <Button type="submit" disabled={!valid || saving} data-testid="liv-save">
              {saving && <Loader2 size={14} className="animate-spin" />} {isNew ? "Créer" : "Enregistrer"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
